/**
 * @voltxampere/web — Onboarding tour state (Sprint 23 C2 + ADR-0046)
 *
 * Pure signal-based state machine. UI reads `tourOpen` + `tourStep`,
 * persisted state (`tourShown`) lives in the IDB settings store
 * (Sprint 23 C1 ADR-0047 paylaşılan). Boot path:
 *
 *   initTour() →
 *     getSettings() →
 *       if !settings?.tour?.shown → startTour()
 *       else → tour stays closed (kullanıcı AYARLAR'dan elle açar)
 *
 * Action API:
 *  - startTour()          — tourOpen=true, tourStep=0
 *  - advanceStep()        — tourStep++; if step >= TOTAL → finish()
 *  - retreatStep()        — tourStep-- (clamps to 0)
 *  - skipTour()           — finish() + persist shown=true
 *  - resetTourState()     — clear shown flag, keep tour closed
 *
 * Test helpers: `__resetTourForTests()` clears signals.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { signal } from "@preact/signals-core";

export const TOTAL_TOUR_STEPS = 6;

export const tourOpen = signal<boolean>(false);
export const tourStep = signal<number>(0);
/**
 * Mirrors `settings.tour.shown`. Hydrated by `initTour` at boot;
 * write-through via `persistTourState`. Keeping it in a signal lets
 * the AYARLAR > "Tour'u tekrar göster" toggle reactively flip the
 * value without a re-render of the entire tour pane.
 */
export const tourShownPersisted = signal<boolean>(false);

export type TourPersister = (shown: boolean, lastStep: number) => Promise<void> | void;

let activePersister: TourPersister | null = null;

/** Wire the IDB persister; called once at boot. Idempotent. */
export function configureTourPersister(persister: TourPersister | null): void {
  activePersister = persister;
}

export function startTour(): void {
  tourStep.value = 0;
  tourOpen.value = true;
}

export function advanceStep(): void {
  const next = tourStep.value + 1;
  if (next >= TOTAL_TOUR_STEPS) {
    finishTour();
    return;
  }
  tourStep.value = next;
}

export function retreatStep(): void {
  const prev = tourStep.value - 1;
  tourStep.value = prev < 0 ? 0 : prev;
}

export function skipTour(): void {
  finishTour();
}

export function finishTour(): void {
  tourOpen.value = false;
  tourStep.value = TOTAL_TOUR_STEPS;
  tourShownPersisted.value = true;
  void persistShown(true, TOTAL_TOUR_STEPS);
}

/**
 * Re-arm the tour from AYARLAR "Tour'u tekrar göster". Clears the
 * persisted shown flag and immediately starts the tour.
 */
export function reArmTour(): void {
  tourShownPersisted.value = false;
  void persistShown(false, 0);
  startTour();
}

async function persistShown(shown: boolean, lastStep: number): Promise<void> {
  if (!activePersister) return;
  try {
    await Promise.resolve(activePersister(shown, lastStep));
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn("[VXA tour] persistShown failed:", err);
    }
  }
}

export function __resetTourForTests(): void {
  tourOpen.value = false;
  tourStep.value = 0;
  tourShownPersisted.value = false;
  activePersister = null;
}
